import type { PricingConfigDTO } from "@/types/pricing";

/**
 * 단가·옵션의 원본은 DB(어드민 → 단가 탭)이고, 웹은 `GET /pricing-config` 로 읽어요.
 * 아래는 API 가 응답하지 못할 때 쓰는 폴백 스냅샷 — apps/api `api/services/pricing.py` 의
 * `DEFAULT_PRICING` 과 같은 값이에요(의도된 중복, 드리프트는 감수).
 * `version: 0` 은 "서버에서 못 받아온 값"이라는 표시로 견적 스냅샷에도 그대로 실려요.
 */
export const FALLBACK_PRICING_CONFIG: PricingConfigDTO = {
  version: 0,
  updated_at: "1970-01-01T00:00:00Z",
  body: {
    project_types: [
      {
        key: "landing",
        label: "랜딩페이지",
        desc: "제품·서비스를 소개하는 한 페이지 중심의 웹사이트예요. 홍보·이벤트·사전예약 페이지에 적합해요.",
        amount: 1_200_000,
        page_amount: 200_000,
      },
      {
        key: "web-app",
        label: "웹앱",
        desc: "로그인·데이터 처리 같은 기능이 있는 웹 애플리케이션이에요. 데이터베이스 설계·연동이 기본으로 들어가요.",
        amount: 4_300_000,
        page_amount: 400_000,
      },
      {
        key: "mobile-app",
        label: "모바일앱",
        desc: "iOS·안드로이드에 설치해 쓰는 앱이에요. 데이터베이스 설계·연동이 기본으로 들어가요.",
        amount: 6_800_000,
        page_amount: 400_000,
      },
    ],
    options: [
      {
        key: "auth",
        label: "인증",
        desc: "회원가입·로그인, 소셜 로그인 같은 사용자 계정 기능이에요.",
        amount: 800_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "identity",
        label: "본인인증",
        desc: "휴대폰·실명 등 본인확인(PortOne·NICE 등) 연동이에요.",
        amount: 900_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "data-store",
        label: "데이터 저장",
        desc: "문의·사전예약 같은 입력을 저장하고 관리해요. 웹앱·모바일앱은 기본으로 들어가 있어요.",
        amount: 700_000,
        group: "feature",
        applies_to: ["landing"],
      },
      {
        key: "payment",
        label: "결제",
        desc: "카드·간편결제 같은 단건 온라인 결제 연동이에요.",
        amount: 2_000_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "subscription",
        label: "정기결제",
        desc: "월·연 단위로 자동 청구되는 구독 결제예요. 단건 결제와는 별개로 붙어요.",
        amount: 1_500_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "admin",
        label: "관리자",
        desc: "콘텐츠·회원·주문 등을 등록·수정·삭제하는 관리자 화면이에요. 차트·지표 화면은 대시보드 옵션이에요.",
        amount: 2_500_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "push",
        label: "알림",
        desc: "앱 푸시·이메일로 알려요. 카카오 알림톡·문자는 알림톡 옵션이에요.",
        amount: 700_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "alimtalk",
        label: "알림톡·문자",
        desc: "카카오 알림톡·문자로 알려요. 발신 프로필 등록과 템플릿 심사가 필요해요.",
        amount: 800_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "map",
        label: "지도",
        desc: "지도 표시·위치 검색 같은 지도 연동이에요.",
        amount: 500_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "i18n",
        label: "다국어",
        desc: "한국어 외 여러 언어를 지원하는 다국어 처리예요.",
        amount: 800_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "llm",
        label: "AI",
        desc: "챗봇·자동 생성 같은 AI(LLM) API 연동이에요. 자체 문서를 근거로 답하는 RAG 구성은 따로 협의해요.",
        amount: 2_500_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "dashboard",
        label: "대시보드",
        desc: "데이터를 차트·지표로 보여주는 대시보드예요.",
        amount: 2_000_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "scheduler",
        label: "주기적 데이터수집",
        desc: "정해진 주기로 외부 데이터를 자동으로 수집·갱신하는 기능이에요(크롤링·스케줄러).",
        amount: 1_500_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "file",
        label: "파일 업로드",
        desc: "이미지·문서 같은 파일 업로드와 저장 기능이에요.",
        amount: 700_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "excel",
        label: "엑셀 입출력",
        desc: "엑셀 파일을 읽어 데이터로 넣거나, 목록을 엑셀로 내려받아요.",
        amount: 800_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "pdf",
        label: "PDF 출력",
        desc: "견적서·보고서 같은 내용을 PDF 문서로 만들어 내려받아요.",
        amount: 700_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "search",
        label: "검색",
        desc: "목록을 키워드로 걸러내는 검색·필터예요. 형태소·의미 기반 검색(Elasticsearch 등)은 따로 협의해요.",
        amount: 600_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "realtime",
        label: "실시간",
        desc: "채팅·알림 등을 실시간으로 동기화하는 기능이에요.",
        amount: 2_000_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "migration",
        label: "데이터 이관",
        desc: "쓰고 있던 시스템·엑셀의 데이터를 새 서비스로 옮겨 넣어요.",
        amount: 1_000_000,
        group: "feature",
        applies_to: [],
      },
      {
        key: "app-bundle",
        label: "앱 동반 개발",
        desc: "웹과 함께 iOS·안드로이드 앱도 같이 개발해요.",
        amount: 7_000_000,
        group: "addon",
        applies_to: ["web-app"],
      },
    ],
    support_tiers: [
      {
        key: "none",
        label: "없음",
        desc: "출시 후 별도 유지보수·운영 지원 없이 인도만 받는 옵션이에요.",
        amount: 0,
      },
      {
        key: "maintenance",
        label: "유지보수",
        desc: "출시 후 버그 수정·업데이트를 월 단위로 지원해요.",
        amount: 300_000,
      },
      {
        key: "managed",
        label: "운영위탁",
        desc: "유지보수에 더해 서버 운영·모니터링·장애 대응까지 저희가 맡아요. (유지보수 포함)",
        amount: 800_000,
      },
    ],
    online_discount_rate: 0.3,
    onetime_cap: 100_000_000,
    max_pages: 50,
    monthly_free_months: 0,
    presets: [
      {
        key: "delivery",
        label: "배달·주문 앱",
        desc: "주문·결제·배차 알림까지 있는 주문 앱이에요. 사장님용 관리자 화면이 함께 들어가요.",
        type: "mobile-app",
        pages: 12,
        options: ["auth", "payment", "push", "map", "admin", "realtime"],
        support: "maintenance",
      },
      {
        key: "booking",
        label: "예약 시스템",
        desc: "샵·스튜디오·클래스 예약을 받고 알림톡으로 안내하는 웹 서비스예요.",
        type: "web-app",
        pages: 10,
        options: ["auth", "payment", "alimtalk", "admin"],
        support: "maintenance",
      },
      {
        key: "shop",
        label: "쇼핑몰",
        desc: "상품을 등록해 팔고 주문을 관리하는 온라인 스토어예요. 상품 검색과 이미지 업로드가 들어가요.",
        type: "web-app",
        pages: 14,
        options: ["auth", "payment", "admin", "search", "file", "alimtalk"],
        support: "maintenance",
      },
      {
        key: "internal-tool",
        label: "사내 관리 도구",
        desc: "엑셀로 관리하던 업무를 옮겨 담는 사내 시스템이에요. 기존 데이터 이관과 지표 화면이 들어가요.",
        type: "web-app",
        pages: 12,
        options: ["auth", "admin", "excel", "dashboard", "file", "search", "migration"],
        support: "maintenance",
      },
      {
        key: "community",
        label: "커뮤니티 앱",
        desc: "글·사진을 올리고 실시간으로 소통하는 커뮤니티 앱이에요. 신고·차단을 다루는 관리자 화면이 함께예요.",
        type: "mobile-app",
        pages: 14,
        options: ["auth", "file", "push", "search", "realtime", "admin"],
        support: "maintenance",
      },
      {
        key: "saas",
        label: "구독 SaaS",
        desc: "월 구독료를 자동으로 청구하는 웹 서비스예요. 사용 지표 대시보드가 들어가요.",
        type: "web-app",
        pages: 12,
        options: ["auth", "subscription", "admin", "dashboard", "file"],
        support: "maintenance",
      },
      {
        key: "ai-chat",
        label: "AI 상담 챗봇",
        desc: "AI 가 문의에 먼저 답하고 사람이 이어받는 상담 서비스예요. 대화 내역과 지표를 볼 수 있어요.",
        type: "web-app",
        pages: 8,
        options: ["auth", "llm", "admin", "dashboard"],
        support: "maintenance",
      },
      {
        key: "landing-preorder",
        label: "랜딩+사전예약",
        desc: "제품을 소개하고 사전예약을 받는 한 장짜리 페이지예요. 신청이 들어오면 알림톡으로 안내해요.",
        type: "landing",
        pages: 3,
        options: ["data-store", "alimtalk"],
        support: "none",
      },
    ],
  },
};
